import React from 'react';
import {
  Card, Table, Button, Form, FormGroup, FormControl, Row, Col, Spinner, Modal
} from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import config from '../../../config';
import { getRoleName } from '../../utils/roleConstants';
import { canAccessUserSettings } from '../../utils/userSettingsAccess';

class UserSettings extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      companies: [],
      selectedCompany: '',
      users: [],
      rolesForCompany: [],
      loadingCompanies: true,
      loadingUsers: false,
      loadingRoles: false,
      savingUser: false,
      updatingRole: {},
      showAddModal: false,
      addForm: {
        username: '',
        password: '',
        email: '',
        firstName: '',
        lastName: '',
        role: ''
      },
      error: null,
      message: null,
      accessDenied: false
    };
  }

  componentDidMount() {
    this.ensureAccessThenInit();
  }

  ensureAccessThenInit = () => {
    const user = this.getStoredUser();
    if (!user) {
      this.setState({ accessDenied: true, loadingCompanies: false });
      return;
    }
    if (user.companyName) {
      if (!canAccessUserSettings(user)) {
        this.setState({ accessDenied: true, loadingCompanies: false });
        return;
      }
      this.fetchCompanies();
      return;
    }
    fetch(
      `${config.apiUrl}/getAccountTarget?account_id=${user.accountId}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' } }
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((account) => {
        const companyName = (account && account.companyName) || '';
        const updated = { ...user, companyName };
        localStorage.setItem('user', JSON.stringify(updated));
        if (!canAccessUserSettings(updated)) {
          this.setState({ accessDenied: true, loadingCompanies: false });
          return;
        }
        this.fetchCompanies();
      })
      .catch(() => this.setState({ accessDenied: true, loadingCompanies: false }));
  };

  getStoredUser = () => {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };

  fetchCompanies = () => {
    this.setState({ loadingCompanies: true, error: null });
    fetch(`${config.apiUrl}/getAccounts`, { method: 'GET', headers: { 'Content-Type': 'application/json' } })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load companies');
        return res.json();
      })
      .then((list) => this.setState({ companies: list || [], loadingCompanies: false }))
      .catch((err) => this.setState({ error: err.message, loadingCompanies: false }));
  };

  onSelectCompany = (e) => {
    const selectedCompany = e.target.value;
    this.setState({ selectedCompany, users: [], rolesForCompany: [] });
    if (!selectedCompany) return;
    this.setState({ loadingUsers: true, loadingRoles: true });
    Promise.all([
      fetch(`${config.apiUrl}/getUsersByCompany?company=${selectedCompany}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }).then((r) => (r.ok ? r.json() : [])),
      fetch(`${config.apiUrl}/getRolesForCompany?company=${selectedCompany}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }).then((r) => (r.ok ? r.json() : { roles: [], modules: [] }))
    ])
      .then(([users, rolesData]) => {
        this.setState({
          users: users || [],
          rolesForCompany: (rolesData && rolesData.roles) ? rolesData.roles : [],
          loadingUsers: false,
          loadingRoles: false
        });
      })
      .catch(() => this.setState({ loadingUsers: false, loadingRoles: false }));
  };

  openAddModal = () => {
    this.setState({
      showAddModal: true,
      addForm: { username: '', password: '', email: '', firstName: '', lastName: '', role: '' },
      message: null
    });
  };

  closeAddModal = () => this.setState({ showAddModal: false, message: null });

  onAddFormChange = (field, value) => {
    this.setState((prev) => ({
      addForm: { ...prev.addForm, [field]: value }
    }));
  };

  submitAddUser = () => {
    const { selectedCompany, addForm, rolesForCompany } = this.state;
    if (!selectedCompany) {
      this.setState({ message: 'Please select a company first.' });
      return;
    }
    const { username, password, email, firstName, lastName, role } = addForm;
    if (!username || !password) {
      this.setState({ message: 'Username and password are required.' });
      return;
    }
    const roleNum = role === '' ? null : parseInt(role, 10);
    if (roleNum === null || isNaN(roleNum) || !rolesForCompany.some((r) => r.role_number === roleNum)) {
      this.setState({ message: 'Please select a valid role for this company.' });
      return;
    }
    this.setState({ savingUser: true, message: null });
    fetch(`${config.apiUrl}/createUser`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: username.trim(),
        password,
        email: (email || '').trim() || null,
        firstName: (firstName || '').trim() || null,
        lastName: (lastName || '').trim() || null,
        company: selectedCompany,
        role: roleNum
      })
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (ok) {
          this.closeAddModal();
          this.onSelectCompany({ target: { value: selectedCompany } });
          this.setState({ message: null });
        } else {
          this.setState({ message: (data && data.error) || 'Failed to create user.' });
        }
      })
      .catch(() => this.setState({ message: 'Request failed. Please try again.' }))
      .finally(() => this.setState({ savingUser: false }));
  };

  updateUserRole = (userId, roleNum) => {
    this.setState((prev) => ({ updatingRole: { ...prev.updatingRole, [userId]: true } }));
    fetch(`${config.apiUrl}/updateUserRole`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, role: roleNum })
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (ok) {
          this.setState((prev) => ({
            users: prev.users.map((u) => (u.id === userId ? { ...u, role: roleNum } : u)),
            updatingRole: { ...prev.updatingRole, [userId]: false }
          }));
        } else {
          window.alert((data && data.error) || 'Failed to update role.');
          this.setState((prev) => ({ updatingRole: { ...prev.updatingRole, [userId]: false } }));
        }
      })
      .catch(() => {
        window.alert('Request failed.');
        this.setState((prev) => ({ updatingRole: { ...prev.updatingRole, [userId]: false } }));
      });
  };

  render() {
    try {
      const user = this.getStoredUser();
      if (!user) return <Navigate to="/login" replace />;
    } catch (e) {
      return <Navigate to="/login" replace />;
    }

    const { accessDenied } = this.state;
    if (accessDenied) {
      return <Navigate to="/" replace />;
    }

    const {
      companies,
      selectedCompany,
      users,
      rolesForCompany,
      loadingCompanies,
      loadingUsers,
      loadingRoles,
      savingUser,
      updatingRole,
      showAddModal,
      addForm,
      error,
      message
    } = this.state;

    return (
      <div className="container">
        <div style={{ marginBottom: '20px' }}>
          <h4>User Settings</h4>
          <p className="text-muted">
            Manage users by company and assign roles according to each company&apos;s subscribed modules.
            Available only to the test company super administrator.
          </p>
        </div>

        {error && (
          <Card className="border-warning mb-3">
            <Card.Body>{error}</Card.Body>
          </Card>
        )}

        <Card className="mb-3">
          <Card.Body>
            <FormGroup as={Row}>
              <Form.Label column sm={2}>Company</Form.Label>
              <Col sm={6}>
                <FormControl
                  as="select"
                  value={selectedCompany}
                  onChange={this.onSelectCompany}
                  disabled={loadingCompanies}
                >
                  <option value="">-- Select company --</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>{c.companyName}</option>
                  ))}
                </FormControl>
              </Col>
            </FormGroup>
          </Card.Body>
        </Card>

        {!selectedCompany && (
          <p className="text-muted">Select a company to list users and manage roles.</p>
        )}

        {selectedCompany && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Users in company</h5>
              <Button variant="primary" size="sm" onClick={this.openAddModal}>
                Add new user
              </Button>
            </div>

            {(loadingUsers || loadingRoles) && (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" /> Loading…
              </div>
            )}

            {!loadingUsers && !loadingRoles && (
              <Card>
                <Card.Body>
                  {users.length === 0 ? (
                    <p className="text-muted mb-0">No users in this company. Click &quot;Add new user&quot; to create one.</p>
                  ) : (
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Username</th>
                          <th>Name</th>
                          <th>Role</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u.id}>
                            <td>
                              <strong>{u.username}</strong>
                              {(u.is_superadmin === true || u.is_superadmin === 1) && (
                                <span className="badge bg-danger ms-1">Superadmin</span>
                              )}
                            </td>
                            <td>{[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}</td>
                            <td>
                              {u.is_superadmin ? (
                                getRoleName(u.role)
                              ) : rolesForCompany.length === 0 ? (
                                getRoleName(u.role)
                              ) : (
                                <FormControl
                                  as="select"
                                  size="sm"
                                  value={u.role}
                                  style={{ width: 'auto', minWidth: '140px' }}
                                  disabled={!!updatingRole[u.id]}
                                  onChange={(e) => this.updateUserRole(u.id, parseInt(e.target.value, 10))}
                                >
                                  {rolesForCompany.map((r) => (
                                    <option key={r.id} value={r.role_number}>{r.role_name}</option>
                                  ))}
                                </FormControl>
                              )}
                            </td>
                            <td>
                              {updatingRole[u.id] && <Spinner animation="border" size="sm" />}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            )}
          </>
        )}

        <Modal show={showAddModal} onHide={this.closeAddModal}>
          <Modal.Header closeButton>
            <Modal.Title>Add new user</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {message && <div className="alert alert-warning">{message}</div>}
            <FormGroup>
              <Form.Label>Username *</Form.Label>
              <FormControl
                value={addForm.username}
                onChange={(e) => this.onAddFormChange('username', e.target.value)}
                placeholder="Username"
              />
            </FormGroup>
            <FormGroup>
              <Form.Label>Password *</Form.Label>
              <FormControl
                type="password"
                value={addForm.password}
                onChange={(e) => this.onAddFormChange('password', e.target.value)}
                placeholder="Password"
              />
            </FormGroup>
            <FormGroup>
              <Form.Label>Email</Form.Label>
              <FormControl
                type="email"
                value={addForm.email}
                onChange={(e) => this.onAddFormChange('email', e.target.value)}
                placeholder="user@example.com"
              />
            </FormGroup>
            <FormGroup>
              <Form.Label>First name</Form.Label>
              <FormControl
                value={addForm.firstName}
                onChange={(e) => this.onAddFormChange('firstName', e.target.value)}
                placeholder="First name"
              />
            </FormGroup>
            <FormGroup>
              <Form.Label>Last name</Form.Label>
              <FormControl
                value={addForm.lastName}
                onChange={(e) => this.onAddFormChange('lastName', e.target.value)}
                placeholder="Last name"
              />
            </FormGroup>
            <FormGroup>
              <Form.Label>Role *</Form.Label>
              <FormControl
                as="select"
                value={addForm.role}
                onChange={(e) => this.onAddFormChange('role', e.target.value)}
              >
                <option value="">-- Select role --</option>
                {rolesForCompany.map((r) => (
                  <option key={r.id} value={r.role_number}>{r.role_name}</option>
                ))}
              </FormControl>
              <small className="text-muted">Roles are based on this company&apos;s subscribed modules.</small>
            </FormGroup>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.closeAddModal}>Cancel</Button>
            <Button variant="primary" onClick={this.submitAddUser} disabled={savingUser}>
              {savingUser ? 'Saving…' : 'Create user'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

export { UserSettings };
